import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AppIcon,
  InfoModal,
  PageHeader,
  useContainerQuery,
  getCollegeIcon,
} from '@backrs_ui/common-ui';

import collegeGuideHeroImage from '@backrs_ui/grow-ui/public/images/college_guide_hero.png';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';

import { Box, Button, Typography, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './CollegeGuidePageContainer.agentContext';
import { collegeGuideContent } from '../content';
import type { CollegeTypeNav, FAQItem } from '../content/types';

type ActiveModal = string | null; // college type id or 'whyMatters'

/**
 * College Guide Page Container
 * Educational guide with college type cards, hero image, and FAQ section
 */
export const CollegeGuidePageContainer: React.FC = () => {
  const navigate = useNavigate();
  const [containerRef, isSidebarHidden] = useContainerQuery(900);

  useAgentContext();

  const [selectedFaqTab, setSelectedFaqTab] = useState<string>(collegeGuideContent.faq[0].id);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const handleFaqTabClick = (faqId: string) => {
    setSelectedFaqTab(faqId);
  };

  const handleCollegeCardClick = (collegeId: string) => {
    setActiveModal(collegeId);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  // Get the active college profile for the modal
  const activeCollegeProfile =
    activeModal && activeModal !== 'whyMatters'
      ? collegeGuideContent.collegeProfiles[
          activeModal as keyof typeof collegeGuideContent.collegeProfiles
        ]
      : null;

  const renderCollegeCard = (collegeType: CollegeTypeNav) => {
    const profile =
      collegeGuideContent.collegeProfiles[
        collegeType.id as keyof typeof collegeGuideContent.collegeProfiles
      ];

    return (
      <Box
        key={collegeType.id}
        sx={collegeCardSx}
        onClick={() => handleCollegeCardClick(collegeType.id)}
      >
        <Box sx={collegeCardHeaderSx}>
          <Box sx={collegeCardIconSx}>
            <AppIcon
              iconName={getCollegeIcon(collegeType.name)}
              diameter="24px"
              color="currentColor"
            />
          </Box>
          <Typography variant="display-xs-semibold" sx={collegeCardTitleSx}>
            {collegeType.displayName}
          </Typography>
          <AppIcon iconName="ChevronRight" diameter="20px" color="Colors/Foreground/fg-secondary" />
        </Box>
        {profile && (
          <Typography variant="text-sm-regular" sx={collegeCardDescriptionSx}>
            {profile.description}
          </Typography>
        )}
      </Box>
    );
  };

  const renderJumpMenu = () => (
    <Box sx={jumpMenuSidebarSx}>
      <Typography variant="text-sm-semibold" sx={jumpToLabelSx}>
        Jump To:
      </Typography>
      <Box sx={tabsContainerSx}>
        {/* Individual FAQ tabs */}
        {collegeGuideContent.faq.map((faq: FAQItem) => (
          <Box
            key={faq.id}
            sx={getTabSx(selectedFaqTab === faq.id)}
            onClick={() => handleFaqTabClick(faq.id)}
          >
            <Typography variant="text-md-semibold" sx={getTabTextSx(selectedFaqTab === faq.id)}>
              {faq.question}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box ref={containerRef} sx={containerSx}>
      {/* PageHeader */}
      <PageHeader
        title="College Guide"
        subtitle="Understand the different colleges within universities and what they mean for your education"
        rightComponents={[
          <Button
            id="college-guide-back-to-majors-btn"
            key="back"
            variant="contained"
            size="md"
            hierarchy="Primary"
            startIcon={<AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />}
            onClick={() => navigate(APP_ROUTE.MAJORS)}
          >
            Back to Majors
          </Button>,
          <Button
            id="college-guide-majors-careers-toggle-btn"
            key="explorer"
            variant="contained"
            size="md"
            hierarchy="Secondary gray"
            onClick={() => navigate(APP_ROUTE.MAJORS_EXPLORER)}
          >
            Majors <AppIcon iconName="SwitchHorizontal01" diameter="16px" color="currentColor" />{' '}
            Careers
          </Button>,
        ]}
        maxWidth={1200}
        showBorder={true}
      />

      {/* Hero Section */}
      <Box sx={heroSectionSx}>
        <Box sx={heroContentSx}>
          <Box sx={heroTextSx}>
            <Typography variant="display-xs-semibold" sx={heroHeadingSx}>
              {collegeGuideContent.intro.title}
            </Typography>
            <Typography variant="body1" sx={heroDescriptionSx}>
              {collegeGuideContent.intro.description}
            </Typography>
            <Button
              id="college-guide-admissions-info-btn"
              variant="contained"
              size="md"
              hierarchy="Secondary gray"
              endIcon={<AppIcon iconName="ArrowUpRight" diameter="20px" color="currentColor" />}
              onClick={() => setActiveModal('whyMatters')}
            >
              Why this matters for admissions
            </Button>
          </Box>
          <Box sx={heroImageCardSx}>
            <Box sx={heroImageWrapperSx}>
              <Box
                component="img"
                src={collegeGuideHeroImage}
                alt="College Guide"
                sx={heroImageSx}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* College Types Grid */}
      <Box sx={collegeTypesContainerSx}>
        <Typography variant="text-lg-semibold" sx={sectionTitleSx}>
          Explore College Types
        </Typography>
        <Box sx={dividerSx} />
        <Box sx={collegeTypesGridSx}>
          {collegeGuideContent.collegeTypes.map((type) => renderCollegeCard(type))}
        </Box>
      </Box>

      {/* FAQ Section */}
      <Box id="faq-section" sx={faqSectionSx}>
        <Box sx={faqHeaderContainerSx}>
          <Typography variant="text-lg-semibold">Frequently Asked Questions</Typography>
          <Typography variant="body1" sx={faqSubtitleSx}>
            Common questions about colleges within universities
          </Typography>
        </Box>
        <Box sx={faqDividerSx} />
        <Box sx={faqContentContainerSx}>
          {/* Jump Menu - hidden on mobile via CSS */}
          {renderJumpMenu()}

          {/* FAQ Items */}
          <Box sx={faqItemsContainerSx}>
            {collegeGuideContent.faq
              .filter((faq) => isSidebarHidden || faq.id === selectedFaqTab)
              .map((faq: FAQItem) => (
                <Box key={faq.id} sx={faqItemContainerSx}>
                  <Typography variant="display-sm-semibold" sx={faqQuestionSx}>
                    {faq.question}
                  </Typography>
                  <Typography variant="body1" sx={faqAnswerSx}>
                    {faq.answer}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>

      {/* Why This Matters Modal */}
      <InfoModal
        open={activeModal === 'whyMatters'}
        onClose={handleCloseModal}
        width="sm"
        featuredIcon={{
          iconName: 'GraduationHat02',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={
          <Typography variant="text-lg-semibold">
            {collegeGuideContent.intro.whyMatters.title}
          </Typography>
        }
        bodyContent={
          <Box sx={whyMattersModalContentSx}>
            {collegeGuideContent.intro.whyMatters.points.map((point, index) => (
              <Box key={index} sx={whyMattersItemSx}>
                <Box sx={checkmarkSx}>
                  <AppIcon
                    iconName="CheckCircle"
                    diameter="20px"
                    color="Colors/Foreground/fg-success-primary"
                  />
                </Box>
                <Typography variant="body2">{point}</Typography>
              </Box>
            ))}
          </Box>
        }
      />

      {/* College Type Details Modal */}
      {activeCollegeProfile && (
        <InfoModal
          open={!!activeCollegeProfile}
          onClose={handleCloseModal}
          width="md"
          featuredIcon={{
            iconName: getCollegeIcon(activeCollegeProfile.name),
            size: 'lg',
            color: 'Colors/Foreground/fg-brand-primary',
          }}
          headerContent={
            <Box sx={collegeModalHeaderSx}>
              <Typography variant="text-lg-semibold">{activeCollegeProfile.displayName}</Typography>
              <Typography variant="body2" sx={collegeModalSubtitleSx}>
                {activeCollegeProfile.description}
              </Typography>
            </Box>
          }
          bodyContent={
            <Box sx={collegeModalContentSx}>
              {/* Key Points */}
              <Box sx={bulletListSx}>
                {activeCollegeProfile.bullets.map((bullet, index) => (
                  <Box key={index} sx={bulletItemSx}>
                    <Box sx={bulletDotSx} />
                    <Typography variant="body2">{bullet}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Key Considerations */}
              <Box sx={considerationsCardSx}>
                <Typography variant="text-md-semibold" sx={considerationsTitleSx}>
                  Key Considerations
                </Typography>
                <Box sx={considerationsGridSx}>
                  {Object.entries(activeCollegeProfile.considerations).map(([key, value]) => (
                    <Box key={key} sx={considerationItemSx}>
                      <Box sx={considerationLabelBadgeSx}>
                        <Typography variant="text-xs-medium" sx={considerationLabelTextSx}>
                          {formatConsiderationLabel(key)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Questions to Ask */}
              <Box sx={questionsSectionSx}>
                <Typography variant="text-md-semibold" sx={questionsTitleSx}>
                  Questions to Ask
                </Typography>
                {activeCollegeProfile.questions.map((question, index) => (
                  <Box key={index} sx={questionItemSx}>
                    <Box sx={questionNumberSx}>
                      <Typography variant="text-sm-semibold">{index + 1}</Typography>
                    </Box>
                    <Typography variant="body2">{question}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          }
        />
      )}
    </Box>
  );
};

// Helper function to format consideration labels
const formatConsiderationLabel = (key: string): string => {
  const labels: Record<string, string> = {
    admissions: 'Admissions',
    prereqs: 'Prerequisites',
    careers: 'Careers',
    experiential: 'Experiential Learning',
    culture: 'Culture',
    workload: 'Workload',
    roi: 'ROI',
    flexibility: 'Flexibility',
  };

  return labels[key] || key;
};

// Styles
const containerSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
});

// Hero Section Styles
const heroSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const heroContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap-reverse',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 900px)': {
    gap: backrs.spacing['spacing-3xl'],
  },
  alignItems: 'stretch',
});

const heroTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  flex: '1 1 min(400px, 100%)',
  minWidth: 0,
  justifyContent: 'center',
});

const heroHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const heroDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const heroImageCardSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 min(400px, 100%)',
  flexShrink: 0,
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const heroImageWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Base/Black'],
  aspectRatio: '3 / 2',
  borderRadius: `calc(${backrs.radius['radius-xl']} - 1px)`,
});

const heroImageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  display: 'block',
  objectFit: 'cover',
};

// College Types Section Styles
const collegeTypesContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  mb: backrs.spacing['spacing-xl'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
  mb: backrs.spacing['spacing-3xl'],
});

const collegeTypesGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
  gap: backrs.spacing['spacing-lg'],
  '@container (min-width: 900px)': {
    gap: backrs.spacing['spacing-2xl'],
  },
  alignItems: 'start',
});

// College Card Styles
const collegeCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-xs-skeuomorphic'],
    borderColor: backrs.colors['Colors/Border/border-brand'],
  },
});

const collegeCardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const collegeCardIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  padding: '12px',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  flexShrink: 0,
});

const collegeCardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  flex: 1,
});

const collegeCardDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

// Jump menu sidebar styles - hidden on mobile
const jumpMenuSidebarSx: SxProps<Theme> = ({ backrs }) => ({
  width: '280px',
  flexShrink: 0,
  display: 'none',
  '@container (min-width: 900px)': {
    display: 'flex',
  },
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const jumpToLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-primary'],
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
});

const tabsContainerSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexDirection: 'column',
});

const getTabSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-md'],
    px: backrs.spacing['spacing-lg'],
    py: backrs.spacing['spacing-md'],
    cursor: 'pointer',
    borderLeft: isActive
      ? `2px solid ${backrs.colors['Colors/Border/border-brand']}`
      : '2px solid transparent',
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    },
  });

const getTabTextSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: isActive
      ? backrs.colors['Colors/Text/text-brand-primary']
      : backrs.colors['Colors/Text/text-quaternary'],
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
  });

// FAQ Section Styles
const faqSectionSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexDirection: 'column',
});

const faqHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const faqSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontSize: '16px',
  lineHeight: '24px',
});

const faqDividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
  mt: backrs.spacing['spacing-xl'],
});

const faqContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-4xl'],
  mt: backrs.spacing['spacing-xl'],
});

const faqItemsContainerSx: SxProps<Theme> = () => ({
  flex: 1,
  minWidth: 0,
});

const faqItemContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  '&:first-of-type': {
    borderTop: 'none',
  },
});

const faqQuestionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const faqAnswerSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

// Why Matters Modal Styles
const whyMattersModalContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const whyMattersItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const checkmarkSx: SxProps<Theme> = {
  flexShrink: 0,
};

// College Modal Styles
const collegeModalHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const collegeModalSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const collegeModalContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const bulletListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const bulletItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const bulletDotSx: SxProps<Theme> = ({ backrs }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-primary'],
  mt: '8px',
  flexShrink: 0,
});

const considerationsCardSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const considerationsTitleSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-lg'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const considerationsGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
  gap: backrs.spacing['spacing-lg'],
});

const considerationItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const considerationLabelBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  px: backrs.spacing['spacing-md'],
  py: backrs.spacing['spacing-xxs'],
  backgroundColor: backrs.colors['Indigo/50'],
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Indigo/200']}`,
  alignSelf: 'flex-start',
});

const considerationLabelTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Indigo/700'],
  textTransform: 'uppercase',
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.5px',
});

const questionsSectionSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Brand/50'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Brand/100']}`,
});

const questionsTitleSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-lg'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const questionItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  mb: backrs.spacing['spacing-md'],
  '&:last-child': {
    mb: 0,
  },
});

const questionNumberSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: backrs.colors['Brand/600'],
  color: backrs.colors['Colors/Base/White'],
  flexShrink: 0,
  fontSize: '12px',
  fontWeight: 600,
});
